import React, { useState, useRef, useEffect, useCallback, useImperativeHandle, forwardRef, useId } from 'react';
import { clsx } from 'clsx';
import { Button } from '@/components/Button';

// Editor content state interface
interface EditorState {
  content: string;
  html: string;
  selection: Range | null;
  history: EditorHistoryState[];
  historyIndex: number;
}

interface EditorHistoryState {
  content: string;
  html: string;
  timestamp: number;
}

// Editor configuration
interface RichTextEditorProps {
  initialContent?: string;
  placeholder?: string;
  onChange?: (content: string, html: string) => void;
  onSelectionChange?: (selection: Range | null) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  disabled?: boolean;
  readOnly?: boolean;
  className?: string;
  autoFocus?: boolean;
  maxLength?: number;
  enableTemplates?: boolean;
  enableMentions?: boolean;
  enableSpellCheck?: boolean;
  enableFullScreen?: boolean;
  templates?: TemplateItem[];
  mentions?: MentionItem[];
  onTemplateSelect?: (template: TemplateItem) => void;
  onMentionSelect?: (mention: MentionItem) => void;
  onImageUpload?: (file: File) => Promise<string>;
  onFileUpload?: (file: File) => Promise<string>;
  autoSaveInterval?: number;
  onAutoSave?: (content: string, html: string) => void;
  /**
   * BugHerd #164: the editable region was a bare div[contenteditable] with no role and no
   * name, so it never appeared in the accessibility tree - assistive technology users could
   * see the toolbar and Send button but not the field itself. These give it a name.
   *
   * label renders a persistent visible caption and names the field via aria-labelledby.
   * ariaLabel names it without visible text, for callers that already show their own heading.
   */
  label?: string;
  ariaLabel?: string;
  /** Instructions associated with the field via aria-describedby. */
  description?: string;
}

interface TemplateItem {
  id: string;
  name: string;
  content: string;
  category?: string;
}

interface MentionItem {
  id: string;
  name: string;
  email?: string;
  avatar?: string;
}

// Editor commands interface
export interface RichTextEditorRef {
  focus: () => void;
  blur: () => void;
  getContent: () => { content: string; html: string };
  setContent: (content: string, html?: string) => void;
  insertText: (text: string) => void;
  insertHTML: (html: string) => void;
  selectAll: () => void;
  undo: () => void;
  redo: () => void;
  formatBold: () => void;
  formatItalic: () => void;
  formatUnderline: () => void;
  formatStrikethrough: () => void;
  insertLink: (url: string, text?: string) => void;
  insertImage: (src: string, alt?: string) => void;
  insertCodeBlock: (language?: string) => void;
  formatHeading: (level: 1 | 2 | 3 | 4 | 5 | 6) => void;
  insertOrderedList: () => void;
  insertUnorderedList: () => void;
  insertTable: (rows: number, cols: number) => void;
  toggleFullScreen: () => void;
}

/**
 * Rich Text Editor Component
 * 
 * Professional WYSIWYG editor with comprehensive formatting, templates,
 * mentions, file uploads, and auto-save functionality
 */
export const RichTextEditor = forwardRef<RichTextEditorRef, RichTextEditorProps>(({
  initialContent = '',
  placeholder = 'Type your message...',
  onChange,
  onSelectionChange,
  onFocus,
  onBlur,
  disabled = false,
  readOnly = false,
  className,
  autoFocus = false,
  maxLength,
  enableTemplates = false,
  enableMentions = false,
  enableSpellCheck = true,
  enableFullScreen = true,
  templates = [],
  mentions = [],
  onTemplateSelect,
  onMentionSelect,
  onImageUpload,
  onFileUpload,
  autoSaveInterval = 30000,
  onAutoSave,
  label,
  ariaLabel,
  description
}, ref) => {
  // Stable ids so the editable region can point at its own visible label and instructions.
  const generatedId = useId();
  const labelId = `rte-label-${generatedId}`;
  const descriptionId = `rte-desc-${generatedId}`;

  // Editor state
  const [editorState, setEditorState] = useState<EditorState>({
    content: initialContent,
    html: initialContent,
    selection: null,
    history: [{ content: initialContent, html: initialContent, timestamp: Date.now() }],
    historyIndex: 0
  });

  const [isFullScreen, setIsFullScreen] = useState(false);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [showImageDialog, setShowImageDialog] = useState(false);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [showMentionsList, setShowMentionsList] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [currentRange, setCurrentRange] = useState<Range | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Refs
  const editorRef = useRef<HTMLDivElement>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const linkUrlRef = useRef<HTMLInputElement>(null);
  const imageUrlRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Initialize editor
  useEffect(() => {
    if (editorRef.current && initialContent) {
      editorRef.current.innerHTML = initialContent;
      setEditorState(prev => ({
        ...prev,
        content: initialContent,
        html: initialContent
      }));
    }
  }, []);

  // Auto-focus if enabled
  useEffect(() => {
    if (autoFocus && editorRef.current) {
      editorRef.current.focus();
    }
  }, [autoFocus]);

  // Auto-save functionality
  useEffect(() => {
    if (onAutoSave && autoSaveInterval > 0) {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }

      autoSaveTimeoutRef.current = setTimeout(() => {
        onAutoSave(editorState.content, editorState.html);
      }, autoSaveInterval);

      return () => {
        if (autoSaveTimeoutRef.current) {
          clearTimeout(autoSaveTimeoutRef.current);
        }
      };
    }
  }, [editorState.content, editorState.html, onAutoSave, autoSaveInterval]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, []);

  // Save current state to history
  const saveToHistory = useCallback((content: string, html: string) => {
    setEditorState(prev => {
      const newHistoryState: EditorHistoryState = {
        content,
        html,
        timestamp: Date.now()
      };

      // Remove future history if we're not at the end
      const newHistory = prev.history.slice(0, prev.historyIndex + 1);
      newHistory.push(newHistoryState);

      // Limit history size
      if (newHistory.length > 100) {
        newHistory.shift();
      }

      return {
        ...prev,
        content,
        html,
        history: newHistory,
        historyIndex: newHistory.length - 1
      };
    });
  }, []);

  // Handle content change
  const handleContentChange = useCallback(() => {
    if (!editorRef.current) return;

    const content = editorRef.current.textContent || '';
    const html = editorRef.current.innerHTML;

    // Check max length
    if (maxLength && content.length > maxLength) {
      return;
    }

    setEditorState(prev => ({ ...prev, content, html }));
    onChange?.(content, html);

    // Save to history after delay
    setTimeout(() => saveToHistory(content, html), 1000);
  }, [maxLength, onChange, saveToHistory]);

  // Handle selection change
  const handleSelectionChange = useCallback(() => {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      setCurrentRange(range);
      onSelectionChange?.(range);
    }
  }, [onSelectionChange]);

  // Format text command
  const execCommand = useCallback((command: string, value?: string) => {
    if (!editorRef.current || readOnly || disabled) return;

    editorRef.current.focus();
    document.execCommand(command, false, value);
    handleContentChange();
  }, [readOnly, disabled, handleContentChange]);

  // Format text with custom implementation for better control
  const formatText = useCallback((format: string, value?: string) => {
    if (!editorRef.current || !currentRange || readOnly || disabled) return;

    try {
      const selection = window.getSelection();
      if (!selection) return;

      selection.removeAllRanges();
      selection.addRange(currentRange);

      switch (format) {
        case 'bold':
          execCommand('bold');
          break;
        case 'italic':
          execCommand('italic');
          break;
        case 'underline':
          execCommand('underline');
          break;
        case 'strikethrough':
          execCommand('strikeThrough');
          break;
        case 'link':
          if (value) {
            execCommand('createLink', value);
          }
          break;
        case 'heading':
          execCommand('formatBlock', `<h${value}>`);
          break;
        case 'paragraph':
          execCommand('formatBlock', '<p>');
          break;
        case 'unorderedList':
          execCommand('insertUnorderedList');
          break;
        case 'orderedList':
          execCommand('insertOrderedList');
          break;
        case 'code':
          execCommand('formatBlock', '<pre>');
          break;
        default:
          execCommand(format, value);
      }
    } catch (error) {
      console.error('Error formatting text:', error);
    }
  }, [currentRange, readOnly, disabled, execCommand]);

  // Insert HTML content
  const insertHTML = useCallback((html: string) => {
    if (!editorRef.current || readOnly || disabled) return;

    editorRef.current.focus();
    execCommand('insertHTML', html);
  }, [readOnly, disabled, execCommand]);

  // Insert mention
  const insertMention = useCallback((mention: MentionItem) => {
    const mentionHTML = `<span class="mention" data-mention-id="${mention.id}" contenteditable="false">@${mention.name}</span>&nbsp;`;
    insertHTML(mentionHTML);
    setShowMentionsList(false);
    setMentionSearch('');
    onMentionSelect?.(mention);
  }, [insertHTML, onMentionSelect]);

  // Handle @ symbol for mentions
  const handleMentionTrigger = useCallback((event: KeyboardEvent) => {
    if (!enableMentions || event.key !== '@') return;

    const selection = window.getSelection();
    if (!selection || !selection.rangeCount) return;

    const range = selection.getRangeAt(0);
    const textNode = range.startContainer;
    const position = range.startOffset;

    // Check if we're at word boundary
    if (textNode.nodeType === Node.TEXT_NODE) {
      const textContent = textNode.textContent || '';
      const charBefore = textContent[position - 1];
      
      if (!charBefore || /\s/.test(charBefore)) {
        setShowMentionsList(true);
        setCurrentRange(range);
      }
    }
  }, [enableMentions]);

  // Handle keyboard shortcuts
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (readOnly || disabled) return;

    // Handle mentions
    if (event.key === '@') {
      handleMentionTrigger(event);
      return;
    }

    // Handle escape to close dialogs
    if (event.key === 'Escape') {
      setShowMentionsList(false);
      setShowTemplateDialog(false);
      setShowLinkDialog(false);
      setShowImageDialog(false);
      return;
    }

    // Handle keyboard shortcuts
    if (event.ctrlKey || event.metaKey) {
      switch (event.key.toLowerCase()) {
        case 'b':
          event.preventDefault();
          formatText('bold');
          break;
        case 'i':
          event.preventDefault();
          formatText('italic');
          break;
        case 'u':
          event.preventDefault();
          formatText('underline');
          break;
        case 'k':
          event.preventDefault();
          setShowLinkDialog(true);
          break;
        case 'z':
          event.preventDefault();
          if (event.shiftKey) {
            redo();
          } else {
            undo();
          }
          break;
        case 'y':
          event.preventDefault();
          redo();
          break;
        case 'a':
          event.preventDefault();
          selectAll();
          break;
        case '/':
          if (enableTemplates) {
            event.preventDefault();
            setShowTemplateDialog(true);
          }
          break;
        case 'enter':
          if (event.shiftKey) {
            // Insert line break instead of paragraph
            event.preventDefault();
            insertHTML('<br>');
          }
          break;
      }
    }
  }, [readOnly, disabled, formatText, enableTemplates, handleMentionTrigger]);

  // Handle paste events
  const handlePaste = useCallback((event: ClipboardEvent) => {
    if (readOnly || disabled) return;

    event.preventDefault();
    
    const clipboardData = event.clipboardData;
    if (!clipboardData) return;

    // Handle image paste
    const items = Array.from(clipboardData.items);
    const imageItem = items.find(item => item.type.startsWith('image/'));
    
    if (imageItem) {
      const file = imageItem.getAsFile();
      if (file && onImageUpload) {
        setIsLoading(true);
        onImageUpload(file)
          .then(imageUrl => {
            insertHTML(`<img src="${imageUrl}" alt="Pasted image" style="max-width: 100%; height: auto;" />`);
          })
          .catch(error => {
            console.error('Error uploading pasted image:', error);
          })
          .finally(() => {
            setIsLoading(false);
          });
        return;
      }
    }

    // Handle text paste
    const text = clipboardData.getData('text/plain');
    const html = clipboardData.getData('text/html');

    if (html) {
      // Clean and insert HTML
      const cleanHTML = html
        .replace(/<script[^>]*>.*?<\/script>/gi, '')
        .replace(/<style[^>]*>.*?<\/style>/gi, '')
        .replace(/on\w+="[^"]*"/gi, '');
      insertHTML(cleanHTML);
    } else if (text) {
      // Insert plain text
      insertHTML(text.replace(/\n/g, '<br>'));
    }
  }, [readOnly, disabled, onImageUpload, insertHTML]);

  // Undo/Redo functionality
  const undo = useCallback(() => {
    setEditorState(prev => {
      if (prev.historyIndex > 0) {
        const newIndex = prev.historyIndex - 1;
        const historyState = prev.history[newIndex];
        
        if (editorRef.current) {
          editorRef.current.innerHTML = historyState.html;
        }
        
        onChange?.(historyState.content, historyState.html);
        
        return {
          ...prev,
          content: historyState.content,
          html: historyState.html,
          historyIndex: newIndex
        };
      }
      return prev;
    });
  }, [onChange]);

  const redo = useCallback(() => {
    setEditorState(prev => {
      if (prev.historyIndex < prev.history.length - 1) {
        const newIndex = prev.historyIndex + 1;
        const historyState = prev.history[newIndex];
        
        if (editorRef.current) {
          editorRef.current.innerHTML = historyState.html;
        }
        
        onChange?.(historyState.content, historyState.html);
        
        return {
          ...prev,
          content: historyState.content,
          html: historyState.html,
          historyIndex: newIndex
        };
      }
      return prev;
    });
  }, [onChange]);

  // Select all text
  const selectAll = useCallback(() => {
    if (editorRef.current) {
      const range = document.createRange();
      range.selectNodeContents(editorRef.current);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
  }, []);

  // Toggle full screen
  const toggleFullScreen = useCallback(() => {
    setIsFullScreen(prev => !prev);
  }, []);

  // Handle file uploads
  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0 || !onFileUpload) return;

    const file = files[0];
    
    // Check if it's an image
    if (file.type.startsWith('image/')) {
      if (onImageUpload) {
        setIsLoading(true);
        onImageUpload(file)
          .then(imageUrl => {
            insertHTML(`<img src="${imageUrl}" alt="${file.name}" style="max-width: 100%; height: auto;" />`);
          })
          .catch(error => {
            console.error('Error uploading image:', error);
          })
          .finally(() => {
            setIsLoading(false);
          });
      }
    } else {
      // Handle other file types
      setIsLoading(true);
      onFileUpload(file)
        .then(fileUrl => {
          insertHTML(`<a href="${fileUrl}" target="_blank" rel="noopener noreferrer">${file.name}</a>`);
        })
        .catch(error => {
          console.error('Error uploading file:', error);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }

    // Reset file input
    event.target.value = '';
  }, [onImageUpload, onFileUpload, insertHTML]);

  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    focus: () => editorRef.current?.focus(),
    blur: () => editorRef.current?.blur(),
    getContent: () => ({ content: editorState.content, html: editorState.html }),
    setContent: (content: string, html?: string) => {
      if (editorRef.current) {
        editorRef.current.innerHTML = html || content;
        setEditorState(prev => ({ ...prev, content, html: html || content }));
      }
    },
    insertText: (text: string) => insertHTML(text.replace(/\n/g, '<br>')),
    insertHTML,
    selectAll,
    undo,
    redo,
    formatBold: () => formatText('bold'),
    formatItalic: () => formatText('italic'),
    formatUnderline: () => formatText('underline'),
    formatStrikethrough: () => formatText('strikethrough'),
    insertLink: (url: string, text?: string) => {
      if (text) {
        insertHTML(`<a href="${url}" target="_blank" rel="noopener noreferrer">${text}</a>`);
      } else {
        formatText('link', url);
      }
    },
    insertImage: (src: string, alt?: string) => {
      insertHTML(`<img src="${src}" alt="${alt || 'Image'}" style="max-width: 100%; height: auto;" />`);
    },
    insertCodeBlock: (language?: string) => {
      const codeHTML = `<pre><code${language ? ` class="language-${language}"` : ''}></code></pre>`;
      insertHTML(codeHTML);
    },
    formatHeading: (level: 1 | 2 | 3 | 4 | 5 | 6) => formatText('heading', level.toString()),
    insertOrderedList: () => formatText('orderedList'),
    insertUnorderedList: () => formatText('unorderedList'),
    insertTable: (rows: number, cols: number) => {
      let tableHTML = '<table border="1" style="border-collapse: collapse; width: 100%;"><tbody>';
      for (let r = 0; r < rows; r++) {
        tableHTML += '<tr>';
        for (let c = 0; c < cols; c++) {
          tableHTML += '<td style="padding: 8px; border: 1px solid #ccc;">&nbsp;</td>';
        }
        tableHTML += '</tr>';
      }
      tableHTML += '</tbody></table>';
      insertHTML(tableHTML);
    },
    toggleFullScreen
  }), [editorState, insertHTML, selectAll, undo, redo, formatText, toggleFullScreen]);

  // Filter mentions based on search
  const filteredMentions = mentions.filter(mention =>
    mention.name.toLowerCase().includes(mentionSearch.toLowerCase())
  );

  return (
    <div 
      className={clsx(
        'rich-text-editor border border-gray-300 rounded-lg bg-white',
        isFullScreen && 'fixed inset-0 z-50 rounded-none',
        disabled && 'opacity-50 pointer-events-none',
        className
      )}
    >
      {/* Toolbar */}
      <div 
        ref={toolbarRef}
        className="flex items-center gap-2 p-2 border-b border-gray-200 bg-gray-50 rounded-t-lg flex-wrap"
      >
        {/* Text Formatting */}
        <div className="flex items-center gap-1 border-r border-gray-300 pr-2 mr-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => formatText('bold')}
            title="Bold (Ctrl+B)"
            className="p-1"
            disabled={readOnly || disabled}
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M6,4h5a4,4 0 0,1 4,4v0a4,4 0 0,1-4,4H6V4M6,14h6a4,4 0 0,1 4,4v0a4,4 0 0,1-4,4H6V14Z" />
            </svg>
          </Button>

          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => formatText('italic')}
            title="Italic (Ctrl+I)"
            className="p-1"
            disabled={readOnly || disabled}
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M10,4V7H12.21L8.79,15H6V18H14V15H11.79L15.21,7H18V4H10Z" />
            </svg>
          </Button>

          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => formatText('underline')}
            title="Underline (Ctrl+U)"
            className="p-1"
            disabled={readOnly || disabled}
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M5,21H19V19H5V21M12,17C15.31,17 18,14.31 18,11V3H16V11C16,13.21 14.21,15 12,15C9.79,15 8,13.21 8,11V3H6V11C6,14.31 8.69,17 12,17Z" />
            </svg>
          </Button>

          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => formatText('strikethrough')}
            title="Strikethrough"
            className="p-1"
            disabled={readOnly || disabled}
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M7.2,9.8C7.2,8.3 8.4,7.1 9.9,7.1C11.4,7.1 12.6,8.3 12.6,9.8H15.6C15.6,6.7 13.1,4.1 9.9,4.1C6.7,4.1 4.2,6.7 4.2,9.8C4.2,11.3 4.9,12.7 6,13.6H18V11.6H13.5C14,10.9 14.3,10.1 14.3,9.2H11.3C11.3,9.9 10.7,10.5 9.9,10.5C9.1,10.5 8.5,9.9 8.5,9.2C8.5,9.1 8.5,9 8.6,8.9H5.6C5.5,9.2 5.4,9.5 5.4,9.8C5.4,12.9 7.9,15.5 11.1,15.5C14.3,15.5 16.8,12.9 16.8,9.8H13.8C13.8,11.3 12.6,12.5 11.1,12.5C9.6,12.5 8.4,11.3 8.4,9.8H5.4Z" />
            </svg>
          </Button>
        </div>

        {/* Headings */}
        <div className="flex items-center gap-2 border-r border-gray-300 pr-2 mr-2">
          <select
            onChange={(e) => {
              const value = e.target.value;
              if (value === 'paragraph') {
                formatText('paragraph');
              } else if (value.startsWith('h')) {
                formatText('heading', value.charAt(1));
              }
            }}
            className="text-sm border border-gray-300 rounded px-3 py-1 pr-8"
            disabled={readOnly || disabled}
          >
            <option value="paragraph">Paragraph</option>
            <option value="h1">Heading 1</option>
            <option value="h2">Heading 2</option>
            <option value="h3">Heading 3</option>
            <option value="h4">Heading 4</option>
            <option value="h5">Heading 5</option>
            <option value="h6">Heading 6</option>
          </select>
        </div>

        {/* Lists */}
        <div className="flex items-center gap-1 border-r border-gray-300 pr-2 mr-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => formatText('unorderedList')}
            title="Bullet List"
            className="p-1"
            disabled={readOnly || disabled}
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M7,5H21V7H7V5M7,13V11H21V13H7M4,4.5A1.5,1.5 0 0,1 5.5,6A1.5,1.5 0 0,1 4,7.5A1.5,1.5 0 0,1 2.5,6A1.5,1.5 0 0,1 4,4.5M4,10.5A1.5,1.5 0 0,1 5.5,12A1.5,1.5 0 0,1 4,13.5A1.5,1.5 0 0,1 2.5,12A1.5,1.5 0 0,1 4,10.5M7,19V17H21V19H7M4,16.5A1.5,1.5 0 0,1 5.5,18A1.5,1.5 0 0,1 4,19.5A1.5,1.5 0 0,1 2.5,18A1.5,1.5 0 0,1 4,16.5Z" />
            </svg>
          </Button>

          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => formatText('orderedList')}
            title="Numbered List"
            className="p-1"
            disabled={readOnly || disabled}
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M7,13V11H21V13H7M7,19V17H21V19H7M7,7V5H21V7H7M3,8V5H2V4H4V8H3M2,17V16H5V20H2V19H4V18.5H3V17.5H4V17H2M4.25,10A0.75,0.75 0 0,1 5,10.75C5,10.95 4.92,11.14 4.79,11.27L3.12,13H5V14H2V13.08L4,11H2V10H4.25Z" />
            </svg>
          </Button>
        </div>

        {/* Links & Media */}
        <div className="flex items-center gap-1 border-r border-gray-300 pr-2 mr-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setShowLinkDialog(true)}
            title="Insert Link (Ctrl+K)"
            className="p-1"
            disabled={readOnly || disabled}
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M3.9,12C3.9,10.29 5.29,8.9 7,8.9H11V7H7A5,5 0 0,0 2,12A5,5 0 0,0 7,17H11V15.1H7C5.29,15.1 3.9,13.71 3.9,12M8,13H16V11H8V13M17,7H13V8.9H17C18.71,8.9 20.1,10.29 20.1,12C20.1,13.71 18.71,15.1 17,15.1H13V17H17A5,5 0 0,0 22,12A5,5 0 0,0 17,7Z" />
            </svg>
          </Button>

          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setShowImageDialog(true)}
            title="Insert Image"
            className="p-1"
            disabled={readOnly || disabled}
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M8.5,13.5L11,16.5L14.5,12L19,18H5M21,19V5C21,3.89 20.1,3 19,3H5A2,2 0 0,0 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19Z" />
            </svg>
          </Button>

          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            title="Upload File"
            className="p-1"
            disabled={readOnly || disabled || !onFileUpload}
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M16.5,6V17.5A4,4 0 0,1 12.5,21.5A4,4 0 0,1 8.5,17.5V5A2.5,2.5 0 0,1 11,2.5A2.5,2.5 0 0,1 13.5,5V15.5A1,1 0 0,1 12.5,16.5A1,1 0 0,1 11.5,15.5V6H10V15.5A2.5,2.5 0 0,0 12.5,18A2.5,2.5 0 0,0 15,15.5V5A4,4 0 0,0 11,1A4,4 0 0,0 7,5V17.5A5.5,5.5 0 0,0 12.5,23A5.5,5.5 0 0,0 18,17.5V6H16.5Z" />
            </svg>
          </Button>
        </div>

        {/* Code & Tables */}
        <div className="flex items-center gap-1 border-r border-gray-300 pr-2 mr-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => formatText('code')}
            title="Code Block"
            className="p-1"
            disabled={readOnly || disabled}
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M8,3A2,2 0 0,0 6,5V9A2,2 0 0,1 4,11H3V13H4A2,2 0 0,1 6,15V19A2,2 0 0,0 8,21H10V19H8V14A2,2 0 0,0 6,12A2,2 0 0,0 8,10V5H10V3M16,3A2,2 0 0,1 18,5V9A2,2 0 0,0 20,11H21V13H20A2,2 0 0,0 18,15V19A2,2 0 0,1 16,21H14V19H16V14A2,2 0 0,1 18,12A2,2 0 0,1 16,10V5H14V3H16Z" />
            </svg>
          </Button>
        </div>

        {/* Undo/Redo */}
        <div className="flex items-center gap-1 border-r border-gray-300 pr-2 mr-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={undo}
            title="Undo (Ctrl+Z)"
            className="p-1"
            disabled={readOnly || disabled || editorState.historyIndex <= 0}
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12.5,8C9.85,8 7.45,9 5.6,10.6L2,7V16H11L7.38,12.38C8.77,11.22 10.54,10.5 12.5,10.5C16.04,10.5 19.05,12.81 20.1,16L22.47,15.22C21.08,11.03 17.15,8 12.5,8Z" />
            </svg>
          </Button>

          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={redo}
            title="Redo (Ctrl+Y)"
            className="p-1"
            disabled={readOnly || disabled || editorState.historyIndex >= editorState.history.length - 1}
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M18.4,10.6C16.55,9 14.15,8 11.5,8C6.85,8 2.92,11.03 1.53,15.22L3.9,16C4.95,12.81 7.96,10.5 11.5,10.5C13.46,10.5 15.23,11.22 16.62,12.38L13,16H22V7L18.4,10.6Z" />
            </svg>
          </Button>
        </div>

        {/* Templates */}
        {enableTemplates && templates.length > 0 && (
          <div className="flex items-center gap-1 border-r border-gray-300 pr-2 mr-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowTemplateDialog(true)}
              title="Templates (Ctrl+/)"
              className="p-1"
              disabled={readOnly || disabled}
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8,12H16V14H8V12M10,20H6V4H13V9H18V12.1L20,10.1V8L14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H10V20Z" />
              </svg>
            </Button>
          </div>
        )}

        {/* Full Screen */}
        {enableFullScreen && (
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={toggleFullScreen}
              title="Full Screen"
              className="p-1"
            >
              {isFullScreen ? (
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M5,16H8V19H5V16M8,8H5V5H8V8M19,8V5H16V8H19M16,19V16H19V19H16M16,13H13V10H16V13M11,13V10H8V13H11M11,8V5H13V8H11M13,19V16H11V19H13Z" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M5,5H10V7H7V10H5V5M14,5H19V10H17V7H14V5M17,14H19V19H14V17H17V14M10,17V19H5V14H7V17H10Z" />
                </svg>
              )}
            </Button>
          </div>
        )}

        {/* Loading indicator */}
        {isLoading && (
          <div className="flex items-center ml-2">
            <svg className="animate-spin h-4 w-4 text-blue-500" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
        )}
      </div>

      {/* Editor Content */}
      {label && (
        <label id={labelId} className="block px-4 pt-3 text-sm font-medium text-gray-700">
          {label}
        </label>
      )}
      {description && (
        <p id={descriptionId} className="px-4 pt-1 text-xs text-gray-500">
          {description}
        </p>
      )}
      <div 
        ref={editorRef}
        className={clsx(
          'min-h-32 max-h-96 overflow-y-auto p-4 focus:outline-none',
          isFullScreen && 'min-h-0 max-h-none flex-1',
          readOnly && 'bg-gray-50'
        )}
        contentEditable={!readOnly && !disabled}
        suppressContentEditableWarning={true}
        // BugHerd #164: without an explicit role and name this region is a bare
        // div[contenteditable] - it carries no accessible name and does not surface as a
        // field, so assistive technology exposed the toolbar and Send button but not the
        // place to type. aria-multiline distinguishes it from a single-line textbox.
        role="textbox"
        aria-multiline="true"
        aria-label={label ? undefined : (ariaLabel || placeholder || 'Rich text editor')}
        aria-labelledby={label ? labelId : undefined}
        aria-describedby={description ? descriptionId : undefined}
        aria-readonly={readOnly || undefined}
        aria-disabled={disabled || undefined}
        tabIndex={readOnly || disabled ? -1 : 0}
        onInput={handleContentChange}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onSelect={handleSelectionChange}
        onFocus={onFocus}
        onBlur={onBlur}
        spellCheck={enableSpellCheck}
        data-placeholder={placeholder}
        style={{
          minHeight: isFullScreen ? 'auto' : '8rem',
          maxHeight: isFullScreen ? 'none' : '24rem'
        }}
      />

      {/* Character count */}
      {maxLength && (
        <div className="px-4 py-2 text-xs text-gray-500 border-t border-gray-200">
          {editorState.content.length} / {maxLength} characters
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        onChange={handleFileSelect}
        accept="image/*,.pdf,.doc,.docx,.txt,.zip"
      />

      {/* Link Dialog */}
      {showLinkDialog && (
        <div className="absolute z-10 mt-2 p-4 bg-white border border-gray-300 rounded-lg shadow-lg">
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Link URL
              </label>
              <input
                ref={linkUrlRef}
                type="url"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="https://example.com"
                autoFocus
              />
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                size="sm"
                onClick={() => {
                  const url = linkUrlRef.current?.value;
                  if (url) {
                    formatText('link', url);
                  }
                  setShowLinkDialog(false);
                }}
              >
                Insert Link
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowLinkDialog(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Image Dialog */}
      {showImageDialog && (
        <div className="absolute z-10 mt-2 p-4 bg-white border border-gray-300 rounded-lg shadow-lg">
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Image URL
              </label>
              <input
                ref={imageUrlRef}
                type="url"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="https://example.com/image.jpg"
                autoFocus
              />
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                size="sm"
                onClick={() => {
                  const url = imageUrlRef.current?.value;
                  if (url) {
                    insertHTML(`<img src="${url}" alt="Image" style="max-width: 100%; height: auto;" />`);
                  }
                  setShowImageDialog(false);
                }}
              >
                Insert Image
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowImageDialog(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Template Dialog */}
      {showTemplateDialog && enableTemplates && (
        <div className="absolute z-10 mt-2 max-w-md bg-white border border-gray-300 rounded-lg shadow-lg max-h-64 overflow-y-auto">
          <div className="p-3 border-b border-gray-200">
            <h3 className="text-sm font-medium text-gray-900">Select Template</h3>
          </div>
          <div className="p-2">
            {templates.map((template) => (
              <button
                key={template.id}
                className="w-full text-left p-2 hover:bg-gray-50 rounded text-sm"
                onClick={() => {
                  insertHTML(template.content);
                  setShowTemplateDialog(false);
                  onTemplateSelect?.(template);
                }}
              >
                <div className="font-medium text-gray-900">{template.name}</div>
                {template.category && (
                  <div className="text-xs text-gray-500">{template.category}</div>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Mentions List */}
      {showMentionsList && enableMentions && (
        <div className="absolute z-10 mt-2 max-w-sm bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          <div className="p-2">
            {filteredMentions.length > 0 ? (
              filteredMentions.map((mention) => (
                <button
                  key={mention.id}
                  className="w-full text-left p-2 hover:bg-gray-50 rounded flex items-center gap-2"
                  onClick={() => insertMention(mention)}
                >
                  {mention.avatar && (
                    <img
                      src={mention.avatar}
                      alt={mention.name}
                      className="w-6 h-6 rounded-full"
                    />
                  )}
                  <div>
                    <div className="font-medium text-gray-900 text-sm">{mention.name}</div>
                    {mention.email && (
                      <div className="text-xs text-gray-500">{mention.email}</div>
                    )}
                  </div>
                </button>
              ))
            ) : (
              <div className="p-2 text-sm text-gray-500">No matches found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
});

RichTextEditor.displayName = 'RichTextEditor';